/**
 * Configuration Interpolation Utilities
 * Handles environment variable substitution in config files
 */

/**
 * Interpolates environment variables in a string
 * Replaces patterns like <ENV_VAR_NAME> with process.env.ENV_VAR_NAME
 *
 * @example
 * interpolateString("<BACKEND_URL>/api") // Returns "https://api.example.com/api"
 */
export function interpolateString(value: string): string {
  return value.replace(/<([A-Z_][A-Z0-9_]*)>/g, (match, envVar) => {
    const envValue = process.env[envVar];
    if (envValue === undefined) {
      // Only warn on server-side, silently return match on client
      if (typeof window === 'undefined') {
        try {
          // Dynamic import to avoid bundling server-only code
          const { loggers } = require('@/lib/logger.server');
          loggers.config.warn({ msg: 'Environment variable not found', envVar, placeholder: match });
        } catch (_) {
          // Ignore if logger not available
        }
      }
      return match;
    }
    return envValue;
  });
}

/**
 * Recursively interpolates environment variables in an object
 *
 * @example
 * interpolateConfig({
 *   api: { baseUrl: "<BACKEND_URL>" }
 * })
 * // Returns: { api: { baseUrl: "https://api.example.com" } }
 */
export function interpolateConfig<T = any>(config: T): T {
  if (typeof config === 'string') {
    return interpolateString(config) as T;
  }

  if (Array.isArray(config)) {
    return config.map((item) => interpolateConfig(item)) as T;
  }

  if (config !== null && typeof config === 'object') {
    const result: any = {};
    for (const [key, value] of Object.entries(config)) {
      result[key] = interpolateConfig(value);
    }
    return result as T;
  }

  return config;
}
